import { POLARIS_NAME } from '../../../copilot.model';
import { Component, inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { AskPolarisWidgetConfig } from '../../../types';
import { AssetVersionPipe } from '../../../../../pipes/asset-version.pipe';
import { PolarisIconComponent } from '../../polaris-icon/polaris-icon.component';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import {DashboardService} from '../../../../../services/dashboard.service';
import {PolarisRuntimeFacadeService} from '../../../../../services/polaris/polaris-runtime-facade.service';
import {
  PolarisStartChatOptionsVm,
  PolarisWidgetContextVm
} from '../../../../../services/polaris/model';
import {Router} from '@angular/router';
import {PolarisRoutePaths, RoutePaths} from '../../../../../shared/types/common';

@Component({
  selector: 'app-ask-polaris-side-drawer',
  imports: [ReactiveFormsModule, AssetVersionPipe, PolarisIconComponent],
  templateUrl: './ask-polaris-side-drawer.component.html',
  styleUrl: './ask-polaris-side-drawer.component.scss'
})
export class AskPolarisSideDrawerComponent implements OnInit {
  private polarisRuntimeFacadeService = inject(PolarisRuntimeFacadeService);
  private router = inject(Router);
  private dashboardService = inject(DashboardService);
  private data = inject(MAT_DIALOG_DATA);
  public widgetQuestions: string[] = [];
  public widgetConfig!: AskPolarisWidgetConfig;
  public polarisName = POLARIS_NAME;
  public customQuestionFormControl = new FormControl<string | null>(null, [Validators.required]);
  public close: () => void = () => {};
  public selectedMetrics: string[] = [];

  ngOnInit(): void {
    const data = this.data.data;
    this.widgetConfig = data.widgetConfig as AskPolarisWidgetConfig;
    this.widgetQuestions = this.widgetConfig.questions;
    this.selectedMetrics = data.selectedMetrics ?? [];
    this.close = data.close;
  }

  onQuestionClicked(question: string): void {
    const options: PolarisStartChatOptionsVm = {
      origin: 'widget',
      widgetContext: this.buildWidgetContext(),
      modelVersion:'v2',
      dataDomain: this.widgetConfig.domain
    };
    void this.sendFromDrawer(question, options);
  }

  onSend(): void {
    const value = this.customQuestionFormControl.value?.trim();
    if (!value) return;
    void this.sendFromDrawer(value, {
      origin: 'widget',
      widgetContext: this.buildWidgetContext(),
      modelVersion: 'v2',
      dataDomain: this.widgetConfig.domain
    });
    this.customQuestionFormControl.setValue('');
  }

  private async sendFromDrawer(prompt: string, options?: PolarisStartChatOptionsVm): Promise<void> {
    this.close();
    await this.delay(500);

    try {
      const filters = this.dashboardService.brandsFilters();
      this.polarisRuntimeFacadeService.setUserBrandContext(
        filters.myBrand.key,
        filters.myCompetitors.map(comp => comp.key)
      );
      const chatId = await this.polarisRuntimeFacadeService.startChat(prompt, options);
      await this.router.navigate([`${RoutePaths.POLARIS}/${PolarisRoutePaths.CHAT}/${chatId}`]);
    } catch {}
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private buildWidgetContext(): PolarisWidgetContextVm {
    const filters = this.dashboardService.brandsFilters();
    const from = this.toIsoDateOnly(filters.dateRange.startDate);
    const to = this.toIsoDateOnly(filters.dateRange.endDate);
    return {
      name: this.widgetConfig.name,
      metrics: this.selectedMetrics,
      myBrand: filters.myBrand.key,
      myCompetitors: filters.myCompetitors.map(comp => comp.key),
      countries: filters.countries,
      device: filters.device,
      dateRange: from && to ? {from, to} : undefined,
    };
  }

  private toIsoDateOnly(date: Date | string | null | undefined): string | undefined {
    const d = date == null ? null : (date instanceof Date ? date : new Date(date));
    if (!d || Number.isNaN(d.getTime())) {
      return undefined;
    }
    return d.toISOString().split('T')[0];
  }
}
